import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { SetupDrawer } from "../components/JackingForce/SetupDrawer";
import { JFPlot } from "../components/JackingForce/JFPlot";
import { SoilSummaryPanel } from "../components/JackingForce/SoilSummaryPanel";
import type { JfConfigBody } from "@tbm/shared";

export function JackingForce() {
  const qc = useQueryClient();
  const [setupOpen, setSetupOpen] = useState(false);

  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const config = useQuery({
    queryKey: ["jf", "config"],
    queryFn: api.jackingForce.config,
  });
  const predictions = useQuery({
    queryKey: ["jf", "predictions"],
    queryFn: api.jackingForce.predictions,
    refetchInterval: 30_000,
  });

  const saveMutation = useMutation({
    mutationFn: (body: JfConfigBody) => api.jackingForce.saveConfig(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jf", "config"] });
      qc.invalidateQueries({ queryKey: ["jf", "predictions"] });
      // Save deletes the cached prediction, so chain a recompute so the
      // user sees fresh output without a separate button click.
      recomputeMutation.mutate();
    },
  });
  const recomputeMutation = useMutation({
    mutationFn: () => api.jackingForce.recompute(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jf", "predictions"] });
    },
  });

  const computedAt = predictions.data?.run?.computedAt;
  const isComputing = saveMutation.isPending || recomputeMutation.isPending;
  const submitError =
    (saveMutation.error as Error | null)?.message ??
    (recomputeMutation.error as Error | null)?.message ??
    null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end justify-between pt-1 flex-wrap gap-3">
        <div>
          <h2 className="text-[13px] font-semibold tracking-[0.18em] uppercase text-text">
            Jacking force prediction
          </h2>
          <div className="text-[11px] text-muted mt-1">
            Predicted vs measured Jacking Force along the drive, from live drive
            data, ground conditions and levels, using the PIML model.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSetupOpen(true)}
            className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded border border-border2 text-text hover:border-wb hover:text-wb"
          >
            Drive setup
          </button>
          {isComputing ? (
            <span className="text-[11px] text-muted inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-wb animate-pulse" />
              <span className="text-text">Computing…</span>
            </span>
          ) : (
            computedAt && (
              <span className="text-[11px] text-muted">
                Last computed:{" "}
                <span className="text-text">
                  {new Date(computedAt).toLocaleString()}
                </span>
              </span>
            )
          )}
        </div>
      </div>

      <SoilSummaryPanel
        payload={predictions.data?.run?.payload ?? null}
        setupSoils={config.data?.soils ?? []}
      />

      <JFPlot
        payload={predictions.data?.run?.payload ?? null}
        driveLengthM={drive.data?.lengthM ?? null}
      />

      <SetupDrawer
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        drive={drive.data ?? null}
        initial={config.data ?? null}
        isSaving={saveMutation.isPending}
        isComputing={recomputeMutation.isPending}
        error={submitError}
        onSave={(body) => saveMutation.mutate(body)}
      />
    </div>
  );
}
